import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTelegramPlane,
  faWhatsapp,
} from '@fortawesome/free-brands-svg-icons';
import {
  MenuOutlined,
  CloseOutlined,
  BellOutlined,
  HomeOutlined,
  UserAddOutlined,
  FileTextOutlined,
  AuditOutlined,
  SafetyCertificateOutlined,
  MailOutlined,
  SearchOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import SiteBrand from './SiteBrand';
import ThemeToggle from './ThemeToggle';
import { useCommunityLinks } from '../../../core/config/useCommunityLinks';
import { isMcpEnabled } from '../../../core/config/runtimeConfig';

const isPathActive = (pathname: string, path: string): boolean =>
  path === '/'
    ? pathname === path
    : pathname === path || pathname.startsWith(`${path}/`);

const mobileLinkClass = (pathname: string, path: string): string =>
  `jo-mobile-link${isPathActive(pathname, path) ? ' is-active' : ''}`;

const MobileMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { whatsappUrl, telegramUrl, contactEmail } = useCommunityLinks();
  const mcpEnabled = isMcpEnabled();

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeMenu, isOpen]);

  const drawer =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <div className="jo-mobile-drawer-overlay" role="presentation">
            <button
              type="button"
              className="jo-mobile-drawer-backdrop"
              aria-label="Close navigation"
              onClick={closeMenu}
            />
            <div
              className="jo-mobile-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Site navigation"
            >
              <div className="jo-divider flex items-center justify-between border-b p-4">
                <SiteBrand
                  to="/"
                  markSize={36}
                  onClick={closeMenu}
                  wordmarkClassName="text-sm font-black uppercase leading-tight tracking-[0.08em]"
                />
                <div className="flex items-center gap-2">
                  <ThemeToggle showLabel />
                  <button
                    type="button"
                    onClick={closeMenu}
                    className="jo-mobile-close-btn rounded-lg px-3 py-2 text-xl"
                    aria-label="Close navigation"
                  >
                    <CloseOutlined />
                  </button>
                </div>
              </div>

              <nav className="jo-divide-y flex flex-1 flex-col overflow-y-auto">
                <Link
                  to="/"
                  className={mobileLinkClass(location.pathname, '/')}
                  aria-current={
                    isPathActive(location.pathname, '/') ? 'page' : undefined
                  }
                  onClick={closeMenu}
                >
                  <HomeOutlined aria-hidden />
                  Home
                </Link>
                <Link
                  to="/jobs"
                  className={mobileLinkClass(location.pathname, '/jobs')}
                  aria-current={
                    isPathActive(location.pathname, '/jobs')
                      ? 'page'
                      : undefined
                  }
                  onClick={closeMenu}
                >
                  <SearchOutlined />
                  Find Jobs
                </Link>
                <Link
                  to="/subscribe"
                  className={mobileLinkClass(location.pathname, '/subscribe')}
                  aria-current={
                    isPathActive(location.pathname, '/subscribe')
                      ? 'page'
                      : undefined
                  }
                  onClick={closeMenu}
                >
                  <BellOutlined />
                  Job Alerts
                </Link>
                <Link
                  to="/improve-your-resume"
                  className={mobileLinkClass(
                    location.pathname,
                    '/improve-your-resume'
                  )}
                  aria-current={
                    isPathActive(location.pathname, '/improve-your-resume')
                      ? 'page'
                      : undefined
                  }
                  onClick={closeMenu}
                >
                  <FileTextOutlined />
                  Improve Resume
                </Link>
                <Link
                  to="/ats-check"
                  className={mobileLinkClass(location.pathname, '/ats-check')}
                  aria-current={
                    isPathActive(location.pathname, '/ats-check')
                      ? 'page'
                      : undefined
                  }
                  onClick={closeMenu}
                >
                  <AuditOutlined />
                  ATS Check
                </Link>
                <Link
                  to="/resume-authenticity"
                  className={mobileLinkClass(
                    location.pathname,
                    '/resume-authenticity'
                  )}
                  aria-current={
                    isPathActive(location.pathname, '/resume-authenticity')
                      ? 'page'
                      : undefined
                  }
                  onClick={closeMenu}
                >
                  <SafetyCertificateOutlined />
                  Authenticity
                </Link>
                <Link
                  to="/sign-up"
                  className={mobileLinkClass(location.pathname, '/sign-up')}
                  aria-current={
                    isPathActive(location.pathname, '/sign-up')
                      ? 'page'
                      : undefined
                  }
                  onClick={closeMenu}
                >
                  <UserAddOutlined />
                  Post Jobs
                </Link>
                {mcpEnabled && (
                  <Link
                    to="/developers"
                    className={mobileLinkClass(
                      location.pathname,
                      '/developers'
                    )}
                    aria-current={
                      isPathActive(location.pathname, '/developers')
                        ? 'page'
                        : undefined
                    }
                    onClick={closeMenu}
                  >
                    <ApiOutlined />
                    MCP for AI assistants
                  </Link>
                )}

                <div className="px-6 py-4">
                  <p className="jo-body-muted mb-3 text-xs font-black uppercase tracking-wide">
                    Community
                  </p>
                  <div className="flex flex-col gap-1">
                    {whatsappUrl && (
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="jo-mobile-link jo-mobile-link--external"
                        onClick={closeMenu}
                      >
                        <FontAwesomeIcon
                          icon={faWhatsapp}
                          className="community-brand-icon--whatsapp"
                        />
                        WhatsApp
                      </a>
                    )}
                    {telegramUrl && (
                      <a
                        href={telegramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="jo-mobile-link jo-mobile-link--external"
                        onClick={closeMenu}
                      >
                        <FontAwesomeIcon
                          icon={faTelegramPlane}
                          className="community-brand-icon--telegram"
                        />
                        Telegram
                      </a>
                    )}
                    {contactEmail && (
                      <a
                        href={`mailto:${contactEmail}`}
                        className="jo-mobile-link jo-mobile-link--external"
                        onClick={closeMenu}
                      >
                        <MailOutlined />
                        Contact
                      </a>
                    )}
                  </div>
                </div>
              </nav>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="lg:hidden">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="jo-mobile-menu-btn rounded-lg px-3 py-2 text-xl shadow-sm focus:outline-none"
        aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={isOpen}
      >
        {isOpen ? <CloseOutlined /> : <MenuOutlined />}
      </button>
      {drawer}
    </div>
  );
};

export default MobileMenu;
